import { PathBuilder, PathPattern, SiteRouteMatch } from '../routing';
import { Route, Switch, RouteComponentProps } from 'react-router-dom';
import { ActivateAccount } from './modules/account/ActivateAccount';
import { Protected } from './modules/auth/Protected';
import { Redirect } from 'react-router-dom';
import WithProfile from './modules/auth/WithProfile';
import { UserProfile } from '../models';
import { SiteContext } from './contexts/SiteContext';
import { makeStyles } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { ErrorPage } from './modules/main/ErrorPage';
import { Toolbar } from './modules/portfolio/Toolbar';
import PortfolioPageLoader from './modules/portfolio/PortfolioPageLoader';
import { SiteDocumentContainer } from './modules/site-document/SiteDocumentContainer';
import { ismVoult } from '../config';
import OptOutPageContainer from './modules/opt-out/OptOutPageContainer';
import Companies from './modules/companies';
import UsersList from './modules/usersList';
import Sites from './modules/sites';
import SiteRoute from './SiteRoute';
import PrivacyPolicy from './modules/privacyPolicy/PrivacyPolicy';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  content: {
    flexGrow: 1,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  contentWithBanner: {
    flexGrow: 1,
    minHeight: 'calc(100vh - 48px)', // Subtract banner height when banner is present (Material-UI Alert default height)
    display: 'flex',
    flexDirection: 'column',
  },
  contentOuter: {
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  banner: {
    textAlign: 'center',
    zIndex: 1100,
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    '& .MuiAlert-root': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    },
    '& .MuiAlert-icon': {
      marginRight: '8px',
      flexShrink: 0,
    },
    '& .MuiAlert-message': {
      textAlign: 'center',
      minWidth: 0,
      fontSize: '0.7rem',
      [theme.breakpoints.up('sm')]: {
        fontSize: '0.875rem',
      },
    },
  },
}));

export const Root = () => {
  const classes = useStyles();

  // Check if we should show the banner (from now until maintenance ends)
  const shouldShowBanner = () => {
    const now = new Date();
    const maintenanceEnd = new Date('2025-09-20T18:00:00Z'); // 2 PM EST = 6 PM UTC

    return now <= maintenanceEnd;
  };

  return (
    <div>
      {shouldShowBanner() && (
        <Alert severity="warning" className={classes.banner}>
          Keystone will be intermittently unavailable on Saturday September 20 between 12 PM and 2
          PM Eastern Standard Time for scheduled maintenance
        </Alert>
      )}
      <Switch>
        <Route path={PathPattern.ACTIVATE_ACCOUNT} exact>
          <ActivateAccount />
        </Route>

        <Route path={PathPattern.AUTH_CALLBACK} exact>
          <Protected>
            <Redirect to={PathBuilder.PORTFOLIO()} />
          </Protected>
        </Route>

        <Route path={PathPattern.MAIN} exact>
          <Redirect to={PathBuilder.PORTFOLIO()} />
        </Route>

        <Route path={PathPattern.PROTECTED}>
          <Protected>
            <WithProfile
              render={(profile: UserProfile) => (
                <Switch>
                  <Route path={PathPattern.DEFAULT_SITE} exact>
                    {profile.sites.length > 1 ? (
                      <Redirect to={PathBuilder.PORTFOLIO()} />
                    ) : (
                      <Redirect to={PathBuilder.SITE(profile.defaultSiteId)} />
                    )}
                  </Route>

                  <Route path={PathPattern.SITE_DOCUMENT_PATH} exact>
                    <SiteContext>
                      <SiteDocumentContainer />
                    </SiteContext>
                  </Route>

                  {ismVoult && (
                    <Route path={PathPattern.OPT_OUT}>
                      <OptOutPageContainer />
                    </Route>
                  )}

                  <Route path={PathPattern.PORTFOLO}>
                    {profile.sites.length === 1 ? (
                      <Redirect to={PathBuilder.SITE(profile.sites[0].siteId)} />
                    ) : (
                      <div className={classes.root}>
                        <main
                          className={
                            shouldShowBanner() ? classes.contentWithBanner : classes.content
                          }
                        >
                          <Toolbar title="Portfolio" profile={profile} />
                          <div className={classes.contentOuter}>
                            <PortfolioPageLoader />
                          </div>
                        </main>
                      </div>
                    )}
                  </Route>

                  <Route path={PathPattern.COMPANIES} exact>
                    <main
                      className={shouldShowBanner() ? classes.contentWithBanner : classes.content}
                    >
                      <Toolbar profile={profile} />
                      <Companies />
                    </main>
                  </Route>

                  <Route path={PathPattern.USERS} exact>
                    <main
                      className={shouldShowBanner() ? classes.contentWithBanner : classes.content}
                    >
                      <Toolbar profile={profile} />
                      <UsersList />
                    </main>
                  </Route>

                  <Route path={PathPattern.SITES} exact>
                    <main
                      className={shouldShowBanner() ? classes.contentWithBanner : classes.content}
                    >
                      <Toolbar profile={profile} />
                      <Sites />
                    </main>
                  </Route>

                  <Route path={PathPattern.PRIVACY_POLICY} exact>
                    <main
                      className={shouldShowBanner() ? classes.contentWithBanner : classes.content}
                    >
                      <Toolbar profile={profile} />
                      <PrivacyPolicy />
                    </main>
                  </Route>

                  <Route
                    path={PathPattern.SITE}
                    // @ts-ignore todo: fix type
                    render={(route: RouteComponentProps<SiteRouteMatch>) => (
                      <SiteContext>
                        <SiteRoute route={route} profile={profile} />
                      </SiteContext>
                    )}
                  />

                  <Route>
                    <ErrorPage message={'Page not found'} />
                  </Route>
                </Switch>
              )}
            />
          </Protected>
        </Route>

        <Route>
          <ErrorPage message={'Page not found'} />
        </Route>
      </Switch>
    </div>
  );
};
